import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Eye, EyeOff, KeyRound, ArrowLeft, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Reset Password page — landed on from the email link
 * (e.g. /reset-password?token=XXX). The token is one-time, expires in 1 hour.
 * After successful reset the user is redirected to /login with the new
 * password ready to use.
 */
export const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const primary = '#10B981';

  useEffect(() => {
    if (!token) setError('This reset link is missing its token. Request a new reset email from the login page.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPw.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (newPw !== confirm) { setError('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: newPw });
      setSuccess(true);
      // Redirect to login after a short success message
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset link is invalid or expired. Request a new one from the login page.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-off-white p-4">
        <Card className="w-full max-w-md shadow-md">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
            <h2 className="text-xl font-semibold text-text-primary mb-1">Password updated</h2>
            <p className="text-sm text-gray-medium mb-4">
              You can now sign in with your new password. Redirecting you to login…
            </p>
            <Link to="/login" className="text-sm font-medium" style={{ color: primary }}>
              Go to login now →
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-off-white p-4">
      <Card className="w-full max-w-md shadow-md">
        <CardHeader className="space-y-1 text-center pb-4">
          <div className="flex justify-center mb-2">
            <div className="rounded-full p-3" style={{ backgroundColor: `${primary}15` }}>
              <KeyRound className="h-6 w-6" style={{ color: primary }} />
            </div>
          </div>
          <CardTitle className="text-2xl font-heading">Set a new password</CardTitle>
          <CardDescription className="text-gray-medium">
            Choose a new password for your Konnect account.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <div>
              <Label htmlFor="rp-new">New password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="rp-new"
                  type={show ? 'text' : 'password'}
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  required
                  minLength={8}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  data-testid="rp-new-input"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                  tabIndex={-1}
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="rp-confirm">Confirm new password</Label>
              <Input
                id="rp-confirm"
                type={show ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                placeholder="Re-type the new password"
                autoComplete="new-password"
                data-testid="rp-confirm-input"
                className="mt-1.5"
              />
              {confirm && newPw !== confirm && (
                <p className="mt-1 text-xs text-red-500">Passwords don't match</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full text-white"
              style={{ backgroundColor: primary }}
              disabled={submitting || !token || newPw.length < 8 || newPw !== confirm}
              data-testid="rp-submit-btn"
            >
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Updating…</> : 'Update password'}
            </Button>
          </form>

          <Link
            to="/login"
            className="mt-6 flex items-center justify-center gap-1 text-sm hover:opacity-80"
            style={{ color: primary }}
          >
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
